import React, { useState, useMemo } from "react";
import BarChart from "./BarChart";
import PieChart from "./PieChart";

interface TemplateChartsProps {
    chartLabels: (string[])[];
    chartData: (number[])[];
    chartTitle: string[] | string;
    campusEvolutions?: { [campus: string]: { years: string[], values: number[] } };
    nuevoIngresoEvolutions?: { years: string[], values: number[] } | null;
    reingresoEvolutions?: { years: string[], values: number[] } | null;
    nationalInternationalData?: { years: string[]; nacionales: number[]; internacionales: number[] } | null;
    originalMaxY?: number;
}

const TemplateMatriculados: React.FC<TemplateChartsProps> = ({
    chartLabels,
    chartData,
    chartTitle,
    campusEvolutions = {},
    nuevoIngresoEvolutions,
    reingresoEvolutions,
    nationalInternationalData,
    originalMaxY,
}) => {
    const initialYear = chartLabels && chartLabels[0] && chartLabels[0].length > 0
        ? chartLabels[0][chartLabels[0].length - 1]
        : null;
    const [selectedYear, setSelectedYear] = useState<string | null>(initialYear);

    const ingresoReingresoChartData = useMemo(() => {
        if (!selectedYear || !nuevoIngresoEvolutions || !reingresoEvolutions) return null;
        const idxNuevo = nuevoIngresoEvolutions.years.findIndex(y => String(y) === String(selectedYear));
        const idxRein = reingresoEvolutions.years.findIndex(y => String(y) === String(selectedYear));
        const nuevoVal = idxNuevo !== -1 ? nuevoIngresoEvolutions.values[idxNuevo] : 0;
        const reinVal = idxRein !== -1 ? reingresoEvolutions.values[idxRein] : 0;
        return [nuevoVal, reinVal];
    }, [selectedYear, nuevoIngresoEvolutions, reingresoEvolutions]);

    const nacionalInternacionalChartData = useMemo(() => {
        if (!selectedYear || !nationalInternationalData) return null;
        const idx = nationalInternationalData.years.findIndex(y => String(y) === String(selectedYear));
        if (idx === -1) return null;
        return [nationalInternationalData.nacionales[idx], nationalInternationalData.internacionales[idx]];
    }, [selectedYear, nationalInternationalData]);

    const campusChartData = useMemo(() => {
        if (!selectedYear || !campusEvolutions || Object.keys(campusEvolutions).length === 0) return null;
        const campuses = Object.keys(campusEvolutions);
        const values = campuses.map(c => {
            const idx = campusEvolutions[c].years.findIndex(y => String(y) === String(selectedYear));
            return idx !== -1 ? campusEvolutions[c].values[idx] : 0;
        });
        return { campuses, values };
    }, [selectedYear, campusEvolutions]);

    return (
        <div className="template-charts">
            {/* Gráfica principal */}
            {chartLabels[0] && (
                <div className="chart-container principal-chart" key={0}>
                    <BarChart
                        labels={chartLabels[0]}
                        data={chartData[0]}
                        title={Array.isArray(chartTitle) ? chartTitle[0] : chartTitle}
                        maxY={originalMaxY}
                        selectedBarIndex={chartLabels[0].indexOf(selectedYear ?? "")}
                        onBarClick={(index) => {
                            const yearClicked = chartLabels[0][index];
                            setSelectedYear(yearClicked);
                        }}
                    />
                </div>
            )}
            <div className="small-charts-row">
                {/* Ingreso vs Reingreso */}
                {ingresoReingresoChartData && (
                    <div className="chart-container small-chart" key="ingresoReingreso">
                        <BarChart
                            labels={["Nuevo Ingreso", "Reingreso"]}
                            data={ingresoReingresoChartData}
                            title={`Nuevo ingreso vs reingreso (${selectedYear})`}
                        />
                    </div>
                )}

                {/* Nacionales vs Internacionales */}
                {nacionalInternacionalChartData && (
                    <div className="chart-container small-chart" key="nacInt">
                        <PieChart
                            labels={["Nacionales", "Internacionales"]}
                            data={nacionalInternacionalChartData}
                            title={`Nacionales vs internacionales (${selectedYear})`}
                        />
                    </div>
                )}

                {/* Campus */}
                {campusChartData && (
                    <div className="chart-container small-chart" key="campus">
                        <BarChart
                            labels={campusChartData.campuses}
                            data={campusChartData.values}
                            title={`Matriculados por campus (${selectedYear})`}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default TemplateMatriculados;